import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, AlertTriangle, Clock, CheckCircle, Send } from 'lucide-react';
import StatCard from '../../components/Dashboard/StatCard';
import { countComplaintGroups } from '../../utils/complaintKpis';
import { useCrud } from '../../hooks/useApi';
import { useQuery } from 'react-query';
import { complaintService, identityService, institutionService, licenseService } from '../../services/api';
import { fetchAllPages } from '../../utils/apiPagination';
import QueryErrorBanner from '../../components/Common/QueryErrorBanner';
import { personDisplayName, shortId } from '../../utils/lookupLabels';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import ComplaintModal from '../../components/Complaint/ComplaintModal';
import ComplaintTable from '../../components/Complaint/ComplaintTable';
import ComplaintFilters from '../../components/Complaint/ComplaintFilters';
import { useTranslation } from 'react-i18next';

const Complaints = () => {
  const { t } = useTranslation('complaints');
  const [searchParams, setSearchParams] = useSearchParams();

  const [showModal, setShowModal] = useState(false);
  const [editingComplaint, setEditingComplaint] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    categoryId: '',
    sortBy: 'data',
    sortOrder: 'desc',
  });

  const { useList: useCategoriesList } = useCrud('categories', complaintService.categoryCrud);
  const { data: categoriesData } = useCategoriesList();
  const categoriesList = Array.isArray(categoriesData) ? categoriesData : categoriesData?.data ?? [];
  const { data: enterprises = [] } = useQuery(
    ['complaint-enterprises'],
    () => fetchAllPages((params) => identityService.getEnterprises(params)),
    { staleTime: 5 * 60 * 1000 }
  );
  const { data: licenses = [] } = useQuery(
    ['complaint-licenses'],
    () => fetchAllPages((params) => licenseService.getLicencas(params)),
    { staleTime: 5 * 60 * 1000 }
  );
  const { data: servidoresRaw = [] } = useQuery(
    ['complaint-servidores'],
    () => fetchAllPages((params) => institutionService.getServidores(params)),
    { staleTime: 5 * 60 * 1000 }
  );
  const { data: personsRaw = [] } = useQuery(
    ['complaint-persons'],
    () => fetchAllPages((params) => identityService.getPersons(params)),
    { staleTime: 5 * 60 * 1000 }
  );
  const servidorOptions = (servidoresRaw || []).map((s) => {
    const person = (personsRaw || []).find((p) => p.id === s.person_id);
    return {
      id: s.id,
      label: s.name || personDisplayName(person) || s.matricula || shortId(s.id),
    };
  });

  const { useList, useCreate, useUpdate, useDelete } = useCrud('complaints', complaintService.complaintCrud);

  const { data: complaintsData, isLoading, error } = useList();
  const complaintsList = Array.isArray(complaintsData) ? complaintsData : complaintsData?.data ?? [];

  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const handleCreate = () => {
    setEditingComplaint(null);
    setShowModal(true);
  };

  useEffect(() => {
    const highlight = (searchParams.get('highlight') || '').trim();
    if (highlight) {
      if (isLoading) return;
      const found = complaintsList.find((item) => String(item.id) === highlight);
      if (found) {
        setEditingComplaint(found);
        setShowModal(true);
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('highlight');
          return next;
        },
        { replace: true }
      );
      return;
    }
    if (searchParams.get('new') !== '1') return;
    setEditingComplaint({
      category_id: searchParams.get('category_id') || '',
      descricao: searchParams.get('descricao') || '',
      cidade: searchParams.get('cidade') || '',
      data: searchParams.get('data') || '',
    });
    setShowModal(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('new');
        next.delete('category_id');
        next.delete('descricao');
        next.delete('cidade');
        next.delete('data');
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams, isLoading, complaintsList]);

  const handleEdit = (complaint) => {
    setEditingComplaint(complaint);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta denúncia?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = async (data, pendingFiles = []) => {
    try {
      let savedId = editingComplaint?.id;
      if (savedId) {
        await updateMutation.mutateAsync({ id: savedId, data });
      } else {
        const created = await createMutation.mutateAsync(data);
        savedId = created?.id || created?.data?.id;
      }
      if (savedId && pendingFiles.length) {
        for (const file of pendingFiles) {
          await complaintService.uploadComplaintFoto(savedId, file);
        }
      }
      setShowModal(false);
      setEditingComplaint(null);
    } catch (err) {
      console.error('Erro ao salvar denúncia:', err);
    }
  };

  const filteredComplaints = complaintsList.filter((c) => {
    const matchesSearch =
      !filters.search ||
      (c.descricao && c.descricao.toLowerCase().includes(filters.search.toLowerCase())) ||
      (c.local && c.local.toLowerCase().includes(filters.search.toLowerCase())) ||
      (c.bairro && c.bairro.toLowerCase().includes(filters.search.toLowerCase())) ||
      (c.cidade && c.cidade.toLowerCase().includes(filters.search.toLowerCase()));
    const matchesStatus = !filters.status || String(c.status) === String(filters.status);
    const matchesCategory = !filters.categoryId || c.category_id === filters.categoryId;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const kpis = countComplaintGroups(complaintsList);

  const sortedComplaints = [...filteredComplaints].sort((a, b) => {
    const aVal = a[filters.sortBy] || a.data || '';
    const bVal = b[filters.sortBy] || b.data || '';
    if (filters.sortOrder === 'desc') return new Date(bVal) - new Date(aVal);
    return new Date(aVal) - new Date(bVal);
  });

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("title")}
          description="Registro e acompanhamento do status de atendimento."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Denúncias', to: '/complaint/complaints' },
            { label: 'Lista' },
          ]}
        >
          <Can permission={P.COMPLAINT_ACCESS}>
            <button
              type="button"
              onClick={handleCreate}
              className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Nova denúncia
            </button>
          </Can>
        </PageHeader>

        {error ? <QueryErrorBanner error={error} /> : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard title="Denúncias ativas" value={kpis.active} hint="Recebidas neste recorte" icon={AlertTriangle} />
          <StatCard title="Em análise" value={kpis.analysis} hint="Aguardando fiscalização" icon={Clock} color="warning" />
          <StatCard title="Encaminhadas" value={kpis.forwarded} hint="Enviadas para equipes" icon={Send} color="primary" />
          <StatCard title="Resolvidas" value={kpis.resolved} hint="Casos encerrados" icon={CheckCircle} color="success" />
        </div>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <ComplaintFilters
              filters={filters}
              onFiltersChange={setFilters}
              categories={categoriesList}
            />
          </div>
          <div className="p-5 sm:p-6">
            <ComplaintTable
              complaints={sortedComplaints}
              categories={categoriesList}
              enterprises={enterprises}
              licenses={licenses}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCreate={handleCreate}
            />
          </div>
        </div>
      </div>

      {showModal && (
        <ComplaintModal
          complaint={editingComplaint}
          categories={categoriesList}
          enterprises={enterprises}
          licenses={licenses}
          servidores={servidorOptions}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            setEditingComplaint(null);
          }}
        />
      )}
    </FormPageShell>
  );
};

export default Complaints;
